import { useState } from 'react';
import { Accordion, AccordionItem, AccordionHeader, AccordionBody, Table } from 'reactstrap';
import { SpringMVCWrapper } from './SpringMVC.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import springmvc1 from './../../../resources/content/springmvc1.png';
import springmvc2 from './../../../resources/content/springmvc2.png';
import springmvc3 from './../../../resources/content/springmvc3.png';
import springmvc4 from './../../../resources/content/springmvc4.png';
import springmvc5 from './../../../resources/content/springmvc5.png';

function SpringMVC() {

   const [open, setOpen] = useState('');  // Track which accordion item is open

   // Function to toggle accordion item
   const toggle = (index: any) => {
      if (open === index) {
         setOpen('');  // Close the item if it's already open
      } else {
         setOpen(index);  // Open the new item
      }
   };

   return (
      <SpringMVCWrapper>
         <header className="entry-header">
            <h1 className="entry-title" itemProp="headline">Spring MVC</h1>
         </header>
         <br />
         <Accordion open={open} toggle={toggle}>
            <AccordionItem>
               <AccordionHeader targetId="1">
                  <span className="fa fa-book"></span>
                  <span style={{ marginLeft: '3vw' }}>Introduction</span>
               </AccordionHeader>
               <AccordionBody accordionId="1">
                  <p className="text-align-justify">The Spring Web MVC framework helps us develop a flexible web application. It provides Model-View-Controller (MVC) architecture and ready components. In addition, the MVC pattern divides the application based on different aspects (input logic, business logic, and UI logic). It also provides a loose coupling between these elements.</p>
                  <ul className="text-align-justify">
                     <li>The <strong>Model</strong> is the input logic, that encapsulates the application data, and in general, they will consist of POJO.</li>
                     <li>The <strong>View</strong> is the UI logic, that renders the model data and generates HTML output that the client’s browser can interpret.</li>
                     <li>The <strong>Controller</strong> is the business logic, that processes user requests and builds an appropriate model, and passes it to the view for rendering.</li>
                  </ul>
                  <figure className="aligncenter size-large is-style-default">
                     <img decoding="async" width="571" height="373" src={springmvc1} alt="" className="lazy-loaded" sizes="(max-width: 571px) 100vw, 571px" />
                  </figure>
                  <ul className="text-align-justify">
                     <li><strong>DispatcherServlet</strong> is a class that receives the incoming request and maps it to the right resource such as controller, model, and view.</li>
                     <li><strong>Model </strong>consists of the data of the application. Data can be either a single object or a collection of multiple objects.</li>
                     <li><strong>Controller</strong> is responsible for the business logic of an application. @Controller annotation marks the class as a controller.</li>
                     <li><strong>View</strong> represents the information in a specific format. Generally, to create a view page, we use JSP+JSTL.</li>
                     <li><strong>Front Controller</strong> is nothing but the DispatcherServlet class. It is responsible for managing the entire flow of the Spring MVC application.</li>
                  </ul>
               </AccordionBody>
            </AccordionItem>
            <AccordionItem>
               <AccordionHeader targetId="2">
                  <span className="fa fa-book"></span>
                  <span style={{ marginLeft: '3vw' }}>The flow of Spring MVC</span>
               </AccordionHeader>
               <AccordionBody accordionId="2">
                  <ul className="text-align-justify">
                     <li>DispatcherServlet consults the HandlerMapping to call the appropriate Controller after receiving an HTTP request.</li>
                     <li>The request goes to the Controller, which then calls the appropriate service methods based on the used GET or POST method. Based on defined business logic, the service method will set model data and returns view name to the DispatcherServlet.</li>
                     <li>The DispatcherServlet takes help from ViewResolver to pickup the defined view for the request.</li>
                     <li>Once the view is final, the DispatcherServlet passes the model data to the view. Finally, it renders the view on the browser.</li>
                  </ul>
               </AccordionBody>
            </AccordionItem>
            <AccordionItem>
               <AccordionHeader targetId="3">
                  <span className="fa fa-book"></span>
                  <span style={{ marginLeft: '3vw' }}>How to create a Spring MVC project ?</span>
               </AccordionHeader>
               <AccordionBody accordionId="3">
                  <ul className="text-align-justify">
                     <li>Load the spring jar(Spring Core, Spring Web, JSP + JSTL) files or add dependencies in the case of Maven to pom.xml file.</li>
                     <li>Create a controller class</li>
                     <li>Provide entry of this controller in the web.xml file (To map requests that you want the DispatcherServlet to handle, by using URL mapping with {'<servlet-mapping>'} tag)</li>
                     <li>Define the bean in a separate XML file</li>
                     <li>Display the message on the JSP page</li>
                     <li>Start the server and deploy the project</li>
                  </ul>
               </AccordionBody>
            </AccordionItem>
            <AccordionItem>
               <AccordionHeader targetId="4">
                  <span className="fa fa-book"></span>
                  <span style={{ marginLeft: '3vw' }}>Some annotations used in Spring MVC</span>
               </AccordionHeader>
               <AccordionBody accordionId="4">
                  <Table striped bordered responsive>
                     <thead>
                        <tr>
                           <th className="text-align-center" style={{ width: '18%' }}>Annotation</th>
                           <th className="text-align-center">Usage</th>
                        </tr>
                     </thead>
                     <tbody>
                        <tr>
                           <td className="text-align-left">@Controller</td>
                           <td className="text-align-left">It indicates that a particular class serves the role of a controller.</td>
                        </tr>
                        <tr>
                           <td className="text-align-left">@RequestMapping</td>
                           <td className="text-align-left">It is used to map a URL to either an entire class or a particular handler method.</td>
                        </tr>
                        <tr>
                           <td className="text-align-left">@RequestParam</td>
                           <td className="text-align-left">It is used to read the form data and bind it automatically to the parameter present in the provided method.</td>
                        </tr>
                        <tr>
                           <td className="text-align-left">@EnableWebMvc</td>
                           <td className="text-align-left">It is used to enable Spring MVC support through a Java configuration class.</td>
                        </tr>
                        <tr>
                           <td className="text-align-left">@PathVariable</td>
                           <td className="text-align-left">It is used to show that a method parameter is bound to a URI template variable.</td>
                        </tr>
                        <tr>
                           <td className="text-align-left">@SessionAttribute</td>
                           <td className="text-align-left">It is used to pass value across different requests through the session.</td>
                        </tr>
                        <tr>
                           <td className="text-align-left">@RequestBody</td>
                           <td className="text-align-left">It is used to show a method parameter bound to the body of the web request</td>
                        </tr>
                        <tr>
                           <td className="text-align-left">@ResponseBody</td>
                           <td className="text-align-left">It is used to show a method return value bound to the web response body.</td>
                        </tr>
                        <tr>
                           <td className="text-align-left">@ExceptionHandler</td>
                           <td className="text-align-left">It is used to handle exceptions thrown by request handling.</td>
                        </tr>
                        <tr>
                           <td className="text-align-left">@CrossOrigin</td>
                           <td className="text-align-left">It is used to enable cross-origin resource sharing only for this specific method.</td>
                        </tr>
                        <tr>
                           <td className="text-align-left">@Repository</td>
                           <td className="text-align-left">It is used to make the unchecked exceptions (thrown from DAO methods) eligible for translation into Spring DataAccessException by importing the DAOs into the DI container.</td>
                        </tr>
                        <tr>
                           <td className="text-align-left">@Service</td>
                           <td className="text-align-left">It’s a good idea to use @Service over @Component in service-layer classes because it specifies intent better.</td>
                        </tr>
                     </tbody>
                  </Table>
               </AccordionBody>
            </AccordionItem>
            <AccordionItem>
               <AccordionHeader targetId="5">
                  <span className="fa fa-book"></span>
                  <span style={{ marginLeft: '3vw' }}>Beans</span>
               </AccordionHeader>
               <AccordionBody accordionId="5">
                  <ul className="text-align-justify">
                     <li>Bean object forms the backbone of your application.</li>
                     <li>Spring IoC container is responsible for instantiating, assembling, and managing the beans.</li>
                     <li>Creation of beans takes place with the help of the configuration metadata that you supply to the container.</li>
                     <li><strong>Bean Scopes :</strong>
                        <ul>
                           <li><strong>singleton</strong>  (default)
                              <ul>
                                 <li>It limits the scope of the bean definition to a single instance per Spring IoC container.</li>
                              </ul>
                           </li>
                           <li><strong>prototype</strong>
                              <ul>
                                 <li>It limits the scope of a single bean definition to have any number of object instances.</li>
                              </ul>
                           </li>
                           <li><strong>request</strong>
                              <ul>
                                 <li>It limits the scope of a bean definition to an HTTP request. </li>
                              </ul>
                           </li>
                           <li><strong>session</strong>
                              <ul>
                                 <li>It limits the scope of a bean definition to an HTTP session. </li>
                              </ul>
                           </li>
                           <li><strong>global-session</strong>
                              <ul>
                                 <li>It limits the scope of a bean definition to a global HTTP session. </li>
                              </ul>
                           </li>
                        </ul>
                     </li>
                  </ul>
               </AccordionBody>
            </AccordionItem>
            <AccordionItem>
               <AccordionHeader targetId="6">
                  <span className="fa fa-book"></span>
                  <span style={{ marginLeft: '3vw' }}>Dependency Injection</span>
               </AccordionHeader>
               <AccordionBody accordionId="6">
                  <ul className="text-align-justify">
                     <li>It is a technique whereby one object (or static method) supplies the dependencies of another object.</li>
                     <li>It is a design pattern that helps us implement IoC.</li>
                     <li>This technique allows the creation of dependent objects outside of a class and provides those objects to a class in different ways. </li>
                     <li>It helps to move the creation and binding of the dependent objects outside of the class that depends on them.</li>
                     <li><strong>Types of DI :</strong>
                        <ul>
                           <li><strong>Constructor-based DI</strong>
                              <ul>
                                 <li>It is a dependency injection where the spring container invokes the class constructor along with some arguments, each argument representing a dependency on the other class.</li>
                              </ul>
                           </li>
                           <li><strong>Setter-based DI</strong>
                              <ul>
                                 <li>It is a dependency injection where after invoking a no-argument constructor or no-argument static factory method, the spring container calls the setter methods on the bean to instantiate the bean.</li>
                              </ul>
                           </li>
                        </ul>
                     </li>
                  </ul>
               </AccordionBody>
            </AccordionItem>
            <AccordionItem>
               <AccordionHeader targetId="7">
                  <span className="fa fa-book"></span>
                  <span style={{ marginLeft: '3vw' }}>IOC Containers</span>
               </AccordionHeader>
               <AccordionBody accordionId="7">
                  <ul className="text-align-justify">
                     <li>The Spring or IOC container is the core of the Spring Framework. </li>
                     <li>It will create the objects, wire them together, configure them, and manage their complete life cycle from creation till destruction.</li>
                     <li>The container will get instructions on what objects to instantiate, configure, and assemble by reading the configuration metadata provided.</li>
                     <li>It makes use of Java POJO classes and configuration metadata to produce a fully configured and executable system or application.</li>
                     <li><strong>Types of a container :</strong>
                        <ul>
                           <li><strong>BeanFactory</strong>
                              <ul>
                                 <li>It provides the basic support for DI and is defined by the interface:</li>
                                 <SyntaxHighlighter language="" style={dracula} customStyle={{ background: 'grey' }} showLineNumbers={false}>
                                    {'org.springframework.beans.factory.BeanFactory'}
                                 </SyntaxHighlighter>
                              </ul>
                           </li>
                           <li><strong>ApplicationContext</strong>
                              <ul>
                                 <li>It adds more enterprise-specific functionality. For instance, it resolves textual messages from a properties file and publishes the application events to interested event listeners. It is defined by the interface: </li>
                                 <SyntaxHighlighter language="" style={dracula} customStyle={{ background: 'grey'}} showLineNumbers={false}>
                                    {'org.springframework.context.ApplicationContext '}
                                 </SyntaxHighlighter>
                              </ul>
                           </li>
                        </ul>
                     </li>
                  </ul>
               </AccordionBody>
            </AccordionItem>
            <AccordionItem>
               <AccordionHeader targetId="8">
                  <span className="fa fa-book"></span>
                  <span style={{ marginLeft: '3vw' }}>Difference between tags annotation-config and component-scan used in web.xml</span>
               </AccordionHeader>
               <AccordionBody accordionId="8">
                  <ul className="text-align-justify">
                     <li><strong>{'<context:annotation-config>'}</strong>
                        <ul>
                           <li>It helps to activate applied annotations in already registered beans in application context.</li>
                        </ul>
                     </li>
                     <li><strong>{'<context:component-scan>'}</strong>
                        <ul>
                           <li>It helps to<strong> </strong>register the beans in context, plus it also scans the annotations inside beans and activate them.</li>
                        </ul>
                     </li>
                  </ul>
               </AccordionBody>
            </AccordionItem>
            <AccordionItem>
               <AccordionHeader targetId="9">
                  <span className="fa fa-book"></span>
                  <span style={{ marginLeft: '3vw' }}>Difference between classes ContextLoaderListener & DispatcherServlet used in web.xml</span>
               </AccordionHeader>
               <AccordionBody accordionId="9">
                  <ul className="text-align-justify">
                     <li><strong>ContextLoaderListener </strong>
                        <ul>
                           <li>It creates root application context. However, the beans in the root context cannot access the beans in child contexts (directly).</li>
                        </ul>
                     </li>
                     <li><strong>DispatcherServlet </strong>
                        <ul>
                           <li>It creates one child application context per servlet entry. In addition, the child contexts can access the beans defined in the root context.</li>
                        </ul>
                     </li>
                  </ul>
               </AccordionBody>
            </AccordionItem>
            <AccordionItem>
               <AccordionHeader targetId="10">
                  <span className="fa fa-book"></span>
                  <span style={{ marginLeft: '3vw' }}>Validation Support in Spring MVC</span>
               </AccordionHeader>
               <AccordionBody accordionId="10">
                  <ul className="text-align-justify">
                     <li>Using JSR-303 Annotations and any reference implementation e.g. Hibernate Validator</li>
                     <li>Using a custom implementation of the interface: </li>
                     <SyntaxHighlighter language="" style={dracula} customStyle={{ background: 'grey'}} showLineNumbers={false}>
                        {'org.springframework.validation.Validator'}
                     </SyntaxHighlighter>
                  </ul>
               </AccordionBody>
            </AccordionItem>
            <AccordionItem>
               <AccordionHeader targetId="11">
                  <span className="fa fa-book"></span>
                  <span style={{ marginLeft: '3vw' }}>Advantages of MVC framework</span>
               </AccordionHeader>
               <AccordionBody accordionId="11">
                  <ul className="text-align-justify">
                     <li>Supports RESTful URLs.</li>
                     <li>Annotation-based configuration</li>
                     <li>Supports to plug with other MVC frameworks like Structs, Structs2, JSF, etc.</li>
                     <li>Flexible, in other words, supports different View types like JSP, Velocity, XML, PDF, Tiles, etc.</li>
                  </ul>
               </AccordionBody>
            </AccordionItem>
            <AccordionItem>
               <AccordionHeader targetId="12">
                  <span className="fa fa-book"></span>
                  <span style={{ marginLeft: '3vw' }}>Spring MVC Hello World Example</span>
               </AccordionHeader>
               <AccordionBody accordionId="12">
                  <ul className="text-align-justify">
                     <li >Create a “Dynamic Web Project” </li>
                     <li >Convert to Maven Project</li>
                     <li >Match your Project Structure</li>
                     <li >Add dependencies in pom.xml
                        <ul>
                           <li >spring-webmvc</li>
                           <li >javax.servlet-api</li>
                        </ul>
                     </li>
                     <li >Add code for the rest of the file as shown below</li>
                  </ul>
                  <figure className="aligncenter size-large" >
                     <img decoding="async" width="361" height="421" src={springmvc2} alt="" className="lazy-loaded" sizes="(max-width: 361px) 100vw, 361px" />
                     <figcaption >Project Structure</figcaption>
                  </figure>
                  <br />
                  <br />
                  <p className="text-align-center" style={{ fontSize: '20px' }}><span className="has-inline-color has-secondary-color" ><em ><strong >pom.xml</strong></em></span></p>
                  <SyntaxHighlighter language="xml" style={dracula} showLineNumbers={true}>
                     {
                        '<project\n' +
                        '	xmlns="http://maven.apache.org/POM/4.0.0"\n' +
                        '	xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"\n' +
                        '    xsi:schemaLocation="http://maven.apache.org/POM/4.0.0 https://maven.apache.org/xsd/maven-4.0.0.xsd">\n' +
                        '	<modelVersion>4.0.0</modelVersion>\n' +
                        '	<groupId>Spring-MVC-HelloWorld-Demo</groupId>\n' +
                        '	<artifactId>Spring-MVC-HelloWorld-Demo</artifactId>\n' +
                        '	<version>0.0.1-SNAPSHOT</version>\n' +
                        '	<packaging>war</packaging>\n' +
                        '	<name>Spring-MVC-HelloWorld-Demo</name>\n' +
                        '	<url>http://maven.apache.org</url>\n' +
                        '	<properties>\n' +
                        '		<maven.compiler.source>1.8</maven.compiler.source>\n' +
                        '		<maven.compiler.target>1.8</maven.compiler.target>\n' +
                        '	</properties>\n' +
                        '	<dependencies>\n' +
                        '		<dependency>\n' +
                        '			<groupId>org.springframework</groupId>\n' +
                        '			<artifactId>spring-webmvc</artifactId>\n' +
                        '			<version>5.0.2.RELEASE</version>\n' +
                        '		</dependency>\n' +
                        '		<dependency>\n' +
                        '			<groupId>javax.servlet</groupId>\n' +
                        '			<artifactId>javax.servlet-api</artifactId>\n' +
                        '			<version>4.0.1</version>\n' +
                        '		</dependency>\n' +
                        '		<dependency>\n' +
                        '			<groupId>junit</groupId>\n' +
                        '			<artifactId>junit</artifactId>\n' +
                        '			<version>3.8.1</version>\n' +
                        '			<scope>test</scope>\n' +
                        '		</dependency>\n' +
                        '	</dependencies>\n' +
                        '	<build>\n' +
                        '		<sourceDirectory>src</sourceDirectory>\n' +
                        '		<plugins>\n' +
                        '			<plugin>\n' +
                        '				<artifactId>maven-compiler-plugin</artifactId>\n' +
                        '				<version>3.8.1</version>\n' +
                        '				<configuration>\n' +
                        '					<release>15</release>\n' +
                        '				</configuration>\n' +
                        '			</plugin>\n' +
                        '			<plugin>\n' +
                        '				<artifactId>maven-war-plugin</artifactId>\n' +
                        '				<version>3.2.3</version>\n' +
                        '				<configuration>\n' +
                        '					<warSourceDirectory>WebContent</warSourceDirectory>\n' +
                        '				</configuration>\n' +
                        '			</plugin>\n' +
                        '		</plugins>\n' +
                        '	</build>\n' +
                        '</project>'
                     }
                  </SyntaxHighlighter>
                  <br />
                  <br />
                  <p className="text-align-center" style={{ fontSize: '20px' }} ><span className="has-inline-color has-secondary-color" ><em ><strong >web.xml</strong></em></span></p>
                  <SyntaxHighlighter language="xml" style={dracula} showLineNumbers={true}>
                     {
                        '<?xml version="1.0" encoding="UTF-8"?>\n' +
                        '<web-app\n' +
                        '	xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"\n' +
                        '	xmlns="http://xmlns.jcp.org/xml/ns/javaee"\n' +
                        '    xsi:schemaLocation="http://xmlns.jcp.org/xml/ns/javaee http://xmlns.jcp.org/xml/ns/javaee/web-app_3_1.xsd"\n' +
                        '    id="WebApp_ID" version="3.1">\n' +
                        '	<absolute-ordering />\n' +
                        '	<!-- Configure Dispatcher Servlet -->\n' +
                        '	<servlet>\n' +
                        '		<servlet-name>dispatcher</servlet-name>\n' +
                        '		<servlet-class>org.springframework.web.servlet.DispatcherServlet</servlet-class>\n' +
                        '		<init-param>\n' +
                        '			<param-name>contextConfigLocation</param-name>\n' +
                        '			<param-value>/WEB-INF/spring-mvc-demo-servlet.xml</param-value>\n' +
                        '		</init-param>\n' +
                        '		<load-on-startup>1</load-on-startup>\n' +
                        '	</servlet>\n' +
                        '	<!-- Set up URL mapping -->\n' +
                        '	<servlet-mapping>\n' +
                        '		<servlet-name>dispatcher</servlet-name>\n' +
                        '		<url-pattern>/</url-pattern>\n' +
                        '	</servlet-mapping>\n' +
                        '</web-app>'
                     }
                  </SyntaxHighlighter>
                  <br />
                  <br />
                  <p className="text-align-center" style={{ fontSize: '20px' }}><span className="has-inline-color has-secondary-color" ><em ><strong >spring-mvc-demo-servlet.xml</strong></em></span></p>
                  <SyntaxHighlighter language="xml" style={dracula} showLineNumbers={true}>
                     {
                        '<?xml version="1.0" encoding="UTF-8"?>\n' +
                        '<beans\n' +
                        '	xmlns="http://www.springframework.org/schema/beans"\n' +
                        '	xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"\n' +
                        '	xmlns:context="http://www.springframework.org/schema/context"\n' +
                        '	xmlns:mvc="http://www.springframework.org/schema/mvc"\n' +
                        '	xmlns:util="http://www.springframework.org/schema/util"\n' +
                        '    xsi:schemaLocation="\n' +
                        '     http://www.springframework.org/schema/beans\n' +
                        '     http://www.springframework.org/schema/beans/spring-beans.xsd\n' +
                        '     http://www.springframework.org/schema/context\n' +
                        '     http://www.springframework.org/schema/context/spring-context-4.1.xsd\n' +
                        '     http://www.springframework.org/schema/mvc\n' +
                        '     http://www.springframework.org/schema/mvc/spring-mvc.xsd\n' +
                        '     http://www.springframework.org/schema/util\n' +
                        '     http://www.springframework.org/schema/util/spring-util.xsd">\n' +
                        '	<!-- Component Scanning -->\n' +
                        '	<context:component-scan base-package="com.misstechack.springmvcdemo"></context:component-scan>\n' +
                        '	<!-- Conversion, formatting, validation support -->\n' +
                        '	<mvc:annotation-driven />\n' +
                        '	<!-- Configure View Resolver -->\n' +
                        '	<bean  className="org.springframework.web.servlet.view.InternalResourceViewResolver">\n' +
                        '		<property name="prefix" value="/WEB-INF/view/"></property>\n' +
                        '		<property name="suffix" value=".jsp"></property>\n' +
                        '	</bean>\n' +
                        '</beans>'
                     }
                  </SyntaxHighlighter>
                  <br />
                  <br />
                  <p className="text-align-center" style={{ fontSize: '20px' }} ><span className="has-inline-color has-secondary-color" ><em ><strong >Model Class – User.java</strong></em></span></p>
                  <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                     {
                        'package com.misstechack.springmvcdemo.model;\n' +
                        '\n' +
                        'public class User {\n' +
                        '  public String userName;\n' +
                        '  public String getUserName() {\n' +
                        '    return userName;\n' +
                        '  }\n' +
                        '  public void setUserName(String userName) {\n' +
                        '    this.userName = userName;\n' +
                        '  }\n' +
                        '}'
                     }
                  </SyntaxHighlighter>
                  <br />
                  <br />
                  <p className="text-align-center" style={{ fontSize: '20px' }}><span className="has-inline-color has-secondary-color" ><em ><strong >Controller Class – HomeController.java</strong></em></span></p>
                  <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
                     {
                        'package com.misstechack.springmvcdemo.controller;\n' +
                        '\n' +
                        'import org.springframework.stereotype.Controller;\n' +
                        'import org.springframework.ui.Model;\n' +
                        'import org.springframework.validation.annotation.Validated;\n' +
                        'import org.springframework.web.bind.annotation.RequestMapping;\n' +
                        'import org.springframework.web.bind.annotation.RequestMethod;\n' +
                        '\n' +
                        'import com.misstechack.springmvcdemo.model.User;\n' +
                        '\n' +
                        '@Controller\n' +
                        'public class HomeController {\n' +
                        '\n' +
                        '  @RequestMapping(value = "/", method = RequestMethod.GET)\n' +
                        '  public String home(Model model) {\n' +
                        '    return "home";\n' +
                        '  }\n' +
                        '  @RequestMapping(value = "/user", method = RequestMethod.POST)\n' +
                        '  public String user(@Validated User user, Model model) {\n' +
                        '    model.addAttribute("userName", user.getUserName());\n' +
                        '    return "user";\n' +
                        '  }\n' +
                        '}'
                     }
                  </SyntaxHighlighter>
                  <br />
                  <br />
                  <p className="text-align-center" style={{ fontSize: '20px' }}><span className="has-inline-color has-secondary-color" ><strong ><em >View – home.jsp</em></strong></span></p>
                  <SyntaxHighlighter language="html" style={dracula} showLineNumbers={true}>
                     {
                        '<%@ page language="java" contentType="text/html; charset=UTF-8" pageEncoding="UTF-8" %>\n' +
                        '<html>\n' +
                        '   <head>\n' +
                        '      <title>Home</title>\n' +
                        '   </head>\n' +
                        '   <body>\n' +
                        '      <h1>Hello World!</h1>\n' +
                        '      <hr>\n' +
                        '      <form action="user" method="post">\n' +
                        '         User Name:\n' +
                        '         <input type="text" name="userName" placeholder="Enter name">\n' +
                        '         <br><br>\n' +
                        '         <input type="submit" value="Submit">\n' +
                        '      </form>\n' +
                        '   </body>\n' +
                        '</html>'
                     }
                  </SyntaxHighlighter>
                  <br />
                  <br />
                  <p className="text-align-center" style={{ fontSize: '20px' }}><span className="has-inline-color has-secondary-color" ><em ><strong >View – user.jsp</strong></em></span></p>
                  <SyntaxHighlighter language="html" style={dracula} showLineNumbers={true}>
                     {
                        '<%@ page language="java" contentType="text/html; charset=UTF-8" pageEncoding="UTF-8" %>\n' +
                        '<!DOCTYPE html PUBLIC "-//W3C//DTD HTML 4.01Transitional//EN" "https://www.w3.org/TR/html4/loose.dtd">\n' +
                        '<html>\n' +
                        '   <head>\n' +
                        '      <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">\n' +
                        '      <title>User Home Page</title>\n' +
                        '   </head>\n' +
                        '   <body>\n' +
                        `      <h3>Hey!! I am ${`userName`}</h3>\n` +
                        '   </body>\n' +
                        '</html>'
                     }
                  </SyntaxHighlighter>
               </AccordionBody>
            </AccordionItem>
            <AccordionItem>
               <AccordionHeader targetId="13">
                  <span className="fa fa-book"></span>
                  <span style={{ marginLeft: '3vw' }}>Steps to execute MVC Application</span>
               </AccordionHeader>
               <AccordionBody accordionId="13">
                  <ul className="text-align-justify">
                     <li >In Eclipse, stop Tomcat</li>
                     <li >
                        Right-click your project and select Export
                        &nbsp;<span className="fa fa-angle-right"></span>&nbsp;
                        WAR File
                     </li>
                     <li >In the Destination field, enter: /Spring-MVC-HelloWorld-Demo.war</li>
                     <li >Outside of Eclipse, start Tomcat</li>
                     <li >If you are using MS Windows, then you should find it on the Start menu</li>
                     <li >Make sure Tomcat is up and running by visiting: http://localhost:8080</li>
                     <li >Deploy your new WAR file by copying it to \webapps</li>
                     <li >Visit your new app. If your war file was: Spring-MVC-HelloWorld-Demo.war then you can access it with http://localhost:8080/Spring-MVC-HelloWorld-Demo/</li>
                  </ul>
                  <br />
                  <figure className="aligncenter size-large" >
                     <img decoding="async" width="491" height="242" src={springmvc3} alt="" className="lazy-loaded" sizes="auto, (max-width: 491px) 100vw, 491px" />
                  </figure>
                  <figure className="aligncenter size-large" >
                     <img decoding="async" width="489" height="260" src={springmvc4} alt="" className="lazy-loaded" sizes="auto, (max-width: 489px) 100vw, 489px" />
                  </figure>
                  <figure className="aligncenter size-large" >
                     <img decoding="async" width="485" height="214" src={springmvc5} alt="" className="lazy-loaded" sizes="auto, (max-width: 485px) 100vw, 485px" />
                  </figure>
               </AccordionBody>
            </AccordionItem>
         </Accordion>
      </SpringMVCWrapper>
   )
}

export default SpringMVC;


